<template>
  <article class="product-card" @click="$emit('click')">
    <div class="card-head">
      <span class="product-type" :class="type">{{ type === 'trial' ? $t('HL体验区') : $t('HL交易区') }}</span>
      <h3>{{ localizedName }}</h3>
    </div>

    <div class="price-row">
      <div>
        <span class="price-label">{{ $t('产品金额') }}</span>
        <div class="price-main">
          <strong>{{ formatAmount(product.price) }}</strong>
          <em>USDT</em>
        </div>
      </div>
      <button class="quick-buy" @click.stop="$emit('buy')">{{ $t('购买') }}</button>
    </div>

    <div class="metric-grid">
      <div class="metric" v-if="type === 'trial'">
        <span>{{ product.period_days }}</span>
        <em>{{ $t('天周期') }}</em>
      </div>
      <div class="metric">
        <span>{{ product.purchase_limit > 0 ? product.purchase_limit : $t('不限') }}</span>
        <em>{{ $t('限购') }}</em>
      </div>
      <div class="metric">
        <span>{{ userPurchaseCount }}</span>
        <em>{{ $t('已购') }}</em>
      </div>
      <div class="metric income">
        <span>{{ formatRate(activeRate) }}%</span>
        <em>{{ $t('日收益率') }}</em>
      </div>
    </div>

    <div class="earnings-strip">
      <span>{{ type === 'trial' ? $t('HL体验区日收益率') : $t('HL交易区日收益率') }}</span>
      <strong>{{ formatRate(activeRate) }}%</strong>
    </div>

    <div class="stock-area" v-if="product.total_stock > 0">
      <div class="stock-meta">
        <span>{{ $t('库存进度') }}</span>
        <em>{{ $t('剩余') }} {{ product.total_stock - product.sold_count }}</em>
      </div>
      <div class="stock-track">
        <div class="stock-fill" :style="{ width: stockPercent + '%' }"></div>
      </div>
    </div>
  </article>
</template>

<script>
import { formatAmount } from '@/utils/web3'

export default {
  name: 'ProductCard',
  props: {
    product: { type: Object, required: true },
    type: { type: String, default: 'trial' }
  },
  computed: {
    userPurchaseCount() {
      return Number(this.product.user_purchase_count || 0)
    },
    localizedName() {
      const value = this.$store.state.locale === 'en-US'
        ? (this.product.name_en || this.product.name)
        : this.product.name
      return value || this.$t('产品')
    },
    activeRate() {
      if (this.type === 'official') {
        const previewRate = Number(this.$store.state.user?.official_earning_preview?.daily_rate || 0)
        if (previewRate > 0) return previewRate
      }
      return Number(this.product.daily_rate || 0)
    },
    stockPercent() {
      if (!this.product.total_stock) return 0
      return Math.min(100, (this.product.sold_count / this.product.total_stock) * 100)
    }
  },
  methods: {
    formatAmount(val) { return formatAmount(val) },
    formatRate(val) {
      return Number(val || 0).toLocaleString('en-US', { maximumFractionDigits: 6 })
    }
  }
}
</script>

<style scoped>
.product-card {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  padding: 18px;
  cursor: pointer;
  transition: transform 0.22s, box-shadow 0.22s, border-color 0.22s;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}
.product-card:hover {
  transform: translateY(-3px);
  border-color: #bfdbfe;
  box-shadow: 0 18px 34px rgba(37, 99, 235, 0.12);
}
.card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.card-head h3 {
  flex: 1;
  color: var(--tech-text);
  font-size: 17px;
  font-weight: 900;
  line-height: 1.35;
  overflow-wrap: anywhere;
}
.product-type {
  order: 2;
  flex-shrink: 0;
  padding: 5px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 850;
}
.product-type.trial { background: #dbeafe; color: #2563eb; }
.product-type.official { background: #fef3c7; color: #b45309; }
.price-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;
  padding: 16px;
  border-radius: 8px;
  background: linear-gradient(135deg, #f8fafc, #eef6ff);
  border: 1px solid #e7eef8;
  margin-bottom: 14px;
}
.price-label {
  display: block;
  color: #64748b;
  font-size: 12px;
  font-weight: 750;
  margin-bottom: 3px;
}
.price-main {
  display: flex;
  align-items: baseline;
  gap: 5px;
}
.price-main strong {
  color: var(--tech-text);
  font-size: 30px;
  line-height: 1;
  font-weight: 950;
}
.price-main em {
  color: #64748b;
  font-size: 12px;
  font-style: normal;
  font-weight: 850;
}
.quick-buy {
  border: none;
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  color: #fff;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  font-weight: 900;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.18);
}
.quick-buy:active { transform: translateY(1px); }
.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 14px;
}
.metric {
  min-width: 0;
  padding: 10px 6px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #edf2f7;
  text-align: center;
}
.metric span {
  display: block;
  color: var(--tech-text);
  font-size: 15px;
  font-weight: 900;
  overflow-wrap: anywhere;
}
.metric em {
  display: block;
  margin-top: 2px;
  color: #94a3b8;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
}
.metric.income {
  background: #ecfdf5;
  border-color: #bbf7d0;
}
.metric.income span { color: #059669; }
.earnings-strip {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 11px 13px;
  border-radius: 8px;
  background: #f0fdfa;
  border: 1px solid #ccfbf1;
  color: #0f766e;
  margin-bottom: 14px;
}
.earnings-strip span { font-size: 12px; font-weight: 750; }
.earnings-strip strong { font-size: 14px; font-weight: 950; white-space: nowrap; }
.stock-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 7px;
  color: #64748b;
  font-size: 11px;
  font-weight: 750;
}
.stock-meta em { font-style: normal; }
.stock-track {
  height: 6px;
  background: #edf2f7;
  border-radius: 999px;
  overflow: hidden;
}
.stock-fill {
  height: 100%;
  background: linear-gradient(90deg, #f59e0b, #ef4444);
  border-radius: 999px;
  transition: width 0.35s;
}
@media (max-width: 640px) {
  .metric-grid { grid-template-columns: repeat(2, 1fr); }
  .price-main strong { font-size: 26px; }
}
</style>
